import React, { useRef, useState } from 'react';
import { Card } from '../../../../components/display/Card';
import { Icon } from '../../../../components/base/Icon';
import { IconDropdown } from '../../list-configuration/columns/IconDropdown';
import { getIconDefinition } from '../../../../components/IconSelector';
import { useThemeColors } from '../../../../providers/ThemeProvider';
import { cn } from '../../../../utils/cn';
import { Text } from '../../../../components/base/Text';
import { Input } from '../../../../components/forms/Input';
import type { FieldConfiguration } from '../../../types';
import {
	faCog,
	faChevronDown,
	faIcons,
} from '@fortawesome/pro-solid-svg-icons';

interface DisplayConfigConfiguratorProps {
	field: FieldConfiguration;
	onUpdateField: (updates: Partial<FieldConfiguration>) => void;
	isModalOpen?: boolean;
}

// Display styles that use displayConfig
const displayConfigStyles = [
	'related_entity_list',
	'badge_list',
	'icon_text',
	'button',
	'actions_dropdown',
];

// Separator options for related entity list
const separatorOptions = [
	{ value: 'line_break', label: 'Line Break' },
	{ value: 'comma', label: 'Comma' },
];

export function DisplayConfigConfigurator({
	field,
	onUpdateField,
	isModalOpen = false,
}: DisplayConfigConfiguratorProps): React.ReactElement | null {
	const colors = useThemeColors();
	const [showIconPicker, setShowIconPicker] = useState(false);
	const iconButtonRef = useRef<HTMLButtonElement>(null);

	// Only show for display styles that use displayConfig
	if (!displayConfigStyles.includes(field.displayStyle)) {
		return null;
	}

	const updateDisplayConfig = (
		updates: Partial<NonNullable<FieldConfiguration['displayConfig']>>
	): void => {
		onUpdateField({
			displayConfig: {
				...field.displayConfig,
				...updates,
			},
		});
	};

	return (
		<Card className="mb-4">
			<div className="p-4">
				<div className="flex items-center mb-3">
					<Icon
						icon={faCog}
						size="sm"
						className={`mr-2 ${colors.classes.textPrimary}`}
					/>
					<Text className="text-base font-semibold">Display Configuration</Text>
				</div>

				{/* Entity Type (for related_entity_list) */}
				{field.displayStyle === 'related_entity_list' && (
					<>
						<div className="mb-4">
							<Text className="text-sm font-medium mb-2">Entity Type</Text>
							<Input
								value={field.displayConfig?.entityType ?? ''}
								onChange={(e) => {
									updateDisplayConfig({ entityType: e.target.value });
								}}
								placeholder="e.g., client, contact, order"
							/>
							<Text className="text-xs mt-1" color="secondary">
								The type of entity being displayed in the list
							</Text>
						</div>

						{/* Name Field */}
						<div className="mb-4">
							<Text className="text-sm font-medium mb-2">Name Field</Text>
							<Input
								value={field.displayConfig?.nameField ?? ''}
								onChange={(e) => {
									updateDisplayConfig({ nameField: e.target.value });
								}}
								placeholder="e.g., name, displayName"
							/>
							<Text className="text-xs mt-1" color="secondary">
								The field to use for the display name of each entity
							</Text>
						</div>

						{/* Link Pattern */}
						<div className="mb-4">
							<Text className="text-sm font-medium mb-2">Link Pattern</Text>
							<Input
								value={field.displayConfig?.linkPattern ?? ''}
								onChange={(e) => {
									updateDisplayConfig({ linkPattern: e.target.value });
								}}
								placeholder="e.g., /clients/{id}"
							/>
							<Text className="text-xs mt-1" color="secondary">
								URL pattern with placeholders. Use {'{id}'} for entity ID.
							</Text>
						</div>

						{/* Separator */}
						<div className="mb-4">
							<Text className="text-sm font-medium mb-2">Separator</Text>
							<div className="flex gap-2">
								{separatorOptions.map((option) => (
									<button
										key={option.value}
										onClick={() => {
											updateDisplayConfig({
												separator: option.value as 'line_break' | 'comma',
											});
										}}
										className={cn(
											'flex-1 py-2 px-3 text-xs rounded-md border-2 text-center transition-colors',
											field.displayConfig?.separator === option.value
												? cn(
														colors.classes.bgInfo,
														colors.classes.borderPrimary,
														colors.classes.textPrimary,
														'font-semibold'
													)
												: cn(
														colors.classes.bgPrimary,
														colors.classes.borderDefault,
														colors.classes.textSecondary,
														'font-medium',
														colors.classes.bgPrimaryHover
													)
										)}
									>
										{option.label}
									</button>
								))}
							</div>
							<Text className="text-xs mt-1" color="secondary">
								How to separate multiple entities in the list
							</Text>
						</div>
					</>
				)}

				{/* Icon Selection (for icon_text, button, related_entity_list) */}
				{['icon_text', 'button', 'related_entity_list'].includes(
					field.displayStyle
				) && (
					<div className="mb-4">
						<Text className="text-sm font-medium mb-2">Icon</Text>
						<button
							ref={iconButtonRef}
							onClick={() => {
								setShowIconPicker(true);
							}}
							className={cn(
								'w-full flex items-center justify-between p-3 border-2 rounded-md',
								colors.classes.bgPrimary,
								colors.classes.borderDefault,
								colors.classes.bgPrimaryHover
							)}
						>
							<div className="flex items-center">
								{field.displayConfig?.icon != null ? (
									<>
										<Icon
											icon={
												getIconDefinition(field.displayConfig.icon) ?? faIcons
											}
											size="sm"
											color={colors.values.textPrimary}
											className="mr-2"
										/>
										<Text className="text-sm">{field.displayConfig.icon}</Text>
									</>
								) : (
									<Text className="text-sm" color="secondary">
										Select an icon...
									</Text>
								)}
							</div>
							<Icon
								icon={faChevronDown}
								size="xs"
								className={colors.classes.textSecondary}
							/>
						</button>

						<IconDropdown
							visible={showIconPicker}
							onClose={() => {
								setShowIconPicker(false);
							}}
							onSelect={(iconName) => {
								updateDisplayConfig({ icon: iconName });
								setShowIconPicker(false);
							}}
							currentIcon={field.displayConfig?.icon}
							triggerRef={iconButtonRef as React.RefObject<HTMLElement>}
							isModalOpen={isModalOpen}
						/>
					</div>
				)}

				{/* Button-specific configuration */}
				{field.displayStyle === 'button' && (
					<div className="mb-4">
						<Text className="text-sm font-medium mb-2">Button Label</Text>
						<Input
							value={field.displayConfig?.buttonLabel ?? ''}
							onChange={(e) => {
								updateDisplayConfig({ buttonLabel: e.target.value });
							}}
							placeholder="e.g., View Details, Edit"
						/>
						<Text className="text-xs mt-1" color="secondary">
							Static text to display on the button
						</Text>
					</div>
				)}

				{/* Actions Dropdown configuration */}
				{field.displayStyle === 'actions_dropdown' && (
					<div className="mb-4">
						<Text className="text-sm font-medium mb-2">Dropdown Label</Text>
						<Input
							value={field.displayConfig?.dropdownLabel ?? ''}
							onChange={(e) => {
								updateDisplayConfig({ dropdownLabel: e.target.value });
							}}
							placeholder="e.g., Actions, Options"
						/>
						<Text className="text-xs mt-1" color="secondary">
							Label for the dropdown trigger button
						</Text>
					</div>
				)}
			</div>
		</Card>
	);
}
